import { Box, Grid, Skeleton, Typography } from '@mui/material'

import { Maybe, PostBoxFragment } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenuProps } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { CellFrame, Template } from '@/core/architecture'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'

import { ShortVideo } from './ShortVideo'

export * from './fragments'

export type State = {
  posts: PostBoxFragment[]
  isMembershipPersonalInformationRequired: boolean
  loading: boolean
  refetching: boolean
  hasNextPage: boolean
  liveStreamId: string | undefined
  user?: Maybe<SideMenuProps['user']>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const ArtistPostVideosTemplate: Template<State, Action> = ({
  state,
  state: { liveStreamId, user },
  ...action
}) => {
  const { t } = useTranslation('artistPostVideos')

  const { router, routerPages, routerNames, generatePath } = useRouter<{
    artistId: string
  }>()
  return (
    <ArtistThemeProvider>
      <Head title={routerNames.ArtistPostVideos} />
      <BaseLayout
        navigation={{
          type: 'artist',
          props: {
            hasManagementTask: !!user?.hasManagementTask,
            unreadNotificationCount: user?.unreadNotificationCount,
            isLive: !!liveStreamId,
          },
        }}
        header={{
          title: t('title'),
          back: true,
          defaultBackPage: generatePath(routerPages.ArtistTop, {
            artistId:
              (config.applicationType === 'standalone'
                ? config.artistId
                : router.query.artistId) ?? '#',
          }),
        }}
      >
        <ShortVideoCell state={state} action={action} />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const ShortVideoCell = ({
  state: {
    posts,
    refetching,
    loading,
    hasNextPage,
    isMembershipPersonalInformationRequired,
  },
  action: { onFetchMore },
}: {
  state: State
  action: Action
}) => {
  const { t } = useTranslation('artistPostVideos')
  const { router, routerPages, generatePath } = useRouter()
  const { showModal } = useSettingUserInfoModal()

  return (
    <CellFrame>
      <ScrollFetcher
        component={Grid}
        componentProps={{
          container: true,
          spacing: 0,
        }}
        itemComponent={Grid}
        itemComponentProps={{
          item: true,
          xs: 4,
        }}
        fadeSx={{ height: '100%' }}
        nodes={posts.map((post, index) => (
          <ShortVideo
            key={index}
            post={post}
            onClick={() => {
              if (
                post.isAccessible &&
                post.isPremium &&
                isMembershipPersonalInformationRequired &&
                post.artist?.patron?.isPersonalInformationRequired
              ) {
                showModal()
              } else {
                router.push(
                  generatePath(routerPages.PostVideoDetailModal, {
                    postId: post.id,
                  })
                )
              }
            }}
          />
        ))}
        onFetchMore={onFetchMore}
        loading={refetching || loading}
        hasNextPage={hasNextPage}
        emptyMessage={<EmptyMessage message={t('postNotExist')} />}
        loadingComponent={
          <Grid container spacing={1} sx={{ width: '100%' }}>
            {Array.from(Array(9).keys()).map(key => (
              <Grid item xs={4} key={key}>
                <Skeleton
                  variant="rounded"
                  sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: '30vh',
                  }}
                />
              </Grid>
            ))}
          </Grid>
        }
      />
    </CellFrame>
  )
}

const EmptyMessage = ({ message }: { message: string }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '85dvh',
      }}
    >
      <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
        {message}
      </Typography>
    </Box>
  )
}
